import { CdkDragDrop, moveItemInArray, transferArrayItem, CdkDropList, CdkDrag } from '@angular/cdk/drag-drop';
import {
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Location, NgIf, NgFor } from '@angular/common';
import { Subscription, debounceTime, distinctUntilChanged, switchMap, forkJoin, firstValueFrom } from 'rxjs';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { UserService } from '../../../../shared/services/user.service';
import { OrderBy, OrderByDirection } from '../../../../shared/utils/constant';
import { AddServiceResponse, DragDropServices } from '../../../models/user-service';
import { OrganizationService } from '../../../services/organization.service';
import { OrganizationServiceResponse } from '../../../models/organization-service-info';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { SlideButtonComponent } from '../../../../shared/components/slide-button/slide-button.component';

const PAGE = 1;
const LIMIT = 10_000;
const SORT_WAY = OrderByDirection.DESC;
const SORT_BY = 'service.id';

const orgServiceFields = [
	'id',
	'service.id',
	'service.labelTranslationId',
	'service.descriptionTransId',
	'service.manageParameter',
	'supplier.id',
	'supplier.name',
	'serviceParameters.id',
	'serviceParameters.value',
	'serviceParameters.parameter.id',
	'serviceParameters.parameter.paramName',
	'serviceParameters.parameter.defaultValue',
	'serviceParameters.parameter.description'
];
@Component({
	selector: 'copo-organization-manage-services',
	templateUrl: './organization-manage-services.component.html',
	animations: pageTransitions,
	styleUrl: './organization-manage-services.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [

		MatButtonModule,
		TranslateModule,
		MatIconModule,
		NgIf,
		CdkDropList,
		NgFor,
		CdkDrag,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		SlideButtonComponent,
		LoadingSpinnerComponent
	]
})

export class OrganizationManageServicesComponent implements OnInit, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;
	@Input() togglePosition = true;
	@Output() readonly typeOfTab = new EventEmitter<string>();
	@Output() readonly togglePositionChange = new EventEmitter<boolean>();
	searchTextControl = new FormControl('');
	allServices: DragDropServices[] = [];
	addedServices: DragDropServices[] = [];
	orgId?: string;
	supplierId?: number;

	isLoading = true;
	routeSub: Subscription = Subscription.EMPTY;
	organizationServices?: OrganizationServiceResponse[];
	addServiceResponse?: AddServiceResponse[];
	manageOrganizationServices?: OrganizationServiceResponse[];
	deleteServiceResponse?: string[];
	searchTextSubscription?: Subscription;
	organizationServiceSub: Subscription = Subscription.EMPTY;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly orgService: OrganizationService,
		private readonly userService: UserService,
		private readonly location: Location
	) {
		if (this.route.parent === null) {
			throw new Error('wrong url');
		}
		this.routeSub = this.route.parent.paramMap.subscribe(params => {
			const idString = params.get('id');
			if (idString === null) {
				return;
			}
			this.orgId = idString;
		});
		if (this.orgId) {
			this.getOrganizationServices(this.orgId);
		}
		const loginUser = this.userService.loggedUser;
		if (loginUser === undefined) {
			return;
		}
		this.supplierId = loginUser.currentVisibility.supplierId;
	}

	ngOnInit() {
		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			const searchText = <string | undefined>value;
			if (searchText === undefined) {
				return;
			}
			if (searchText === '' && this.organizationServices) {
				this.getDragDropForOrg(this.organizationServices);
			} else {
				this.allServices = this.allServices.filter(allSer => allSer.id === searchText
					|| allSer.name.toLowerCase().includes(searchText.toLowerCase()));
			}
		});
	}

	goBack() {
		this.location.back();
	}

	async onSave() {
		if (this.orgId && this.manageOrganizationServices) {
			const filteredResponse =
				this.manageOrganizationServices.filter(o1 => !this.addedServices.some(o2 => o1.service.id === o2.id));
			if (filteredResponse.length > 0) {
				this.deleteServiceResponse = [];
				for (const fr of filteredResponse) {
					this.deleteServiceResponse.push(fr.service.id);
				}
			}
			this.addServiceResponse =
				this.addedServices.filter(o1 =>
					this.manageOrganizationServices && !this.manageOrganizationServices.some(
						o2 => o1.id === o2.service.id
					));
			if (this.addServiceResponse.length > 0 && this.supplierId) {
				await firstValueFrom(this.orgService
					.addServicesToOrganization(
						this.orgId,
						this.supplierId,
						this.addServiceResponse
					));
				if (filteredResponse.length <= 0) {
					this.goBack();
				}
			}
			if (this.deleteServiceResponse && this.deleteServiceResponse.length > 0 && this.supplierId) {
				await firstValueFrom(this.orgService.deleteServicesFromOrganization(
					this.orgId,
					this.supplierId,
					this.deleteServiceResponse
				));
				this.goBack();
			}
			if (this.addServiceResponse.length === 0
				&& (!this.deleteServiceResponse || this.deleteServiceResponse.length === 0)) {
				this.goBack();
			}
		}
	}

	ngOnDestroy() {
		this.organizationServiceSub.unsubscribe();
		this.routeSub.unsubscribe();
	}

	getOrganizationServices(orgId: string) {
		this.organizationServiceSub = forkJoin([
			this.orgService.getOrganizationParentId(orgId).pipe(switchMap(organizationParentId =>
				this.orgService.getOrganizationServices(
					String(organizationParentId),
					orgServiceFields,
					'',
					PAGE,
					LIMIT,
					new OrderBy(<keyof OrganizationServiceResponse>SORT_BY, SORT_WAY),
					false
				))),
			this.orgService.getOrganizationServices(
				orgId,
				orgServiceFields,
				'',
				PAGE,
				LIMIT,
				new OrderBy(<keyof OrganizationServiceResponse>SORT_BY, SORT_WAY),
				false
			)
		]).subscribe({
			next: ([parentServices, currentServices]) => {
				this.manageOrganizationServices = currentServices.results;
				this.addedServices = [];
				for (const service of this.manageOrganizationServices) {
					const dragDropService: DragDropServices = {
						id: service.service.id,
						name: service.service.labelTranslationId,
						description: service.service.descriptionTransId,
					};
					this.addedServices.push(dragDropService);
				}
				this.sortAllSelectedService(this.addedServices);
				this.organizationServices = parentServices.results;
				this.organizationServices = this.organizationServices.filter(o1 =>
					this.manageOrganizationServices
					&& !this.manageOrganizationServices.some(o2 => o1.service.id === o2.service.id));
				this.getDragDropForOrg(this.organizationServices);
				this.isLoading = false;
			},
			error: (err: unknown) => {
				console.error(err);
				this.isLoading = false;
			}
		});
	}

	getDragDropForOrg(orgResponse: OrganizationServiceResponse[]) {
		this.allServices = [];
		for (const service of orgResponse) {
			const dragDropService: DragDropServices = {
				id: service.service.id,
				name: service.service.labelTranslationId,
				description: service.service.descriptionTransId,
			};
			this.allServices.push(dragDropService);
		}
		this.sortAllSelectedService(this.allServices);
	}

	drop(event: CdkDragDrop<DragDropServices[]>) {
		if (event.previousContainer === event.container) {
			moveItemInArray(
				event.container.data,
				event.previousIndex,
				event.currentIndex
			);
		} else {
			transferArrayItem(
				event.previousContainer.data,
				event.container.data,
				event.previousIndex,
				event.currentIndex
			);
		}
	}

	onToggleChange() {
		this.typeOfTab.emit('Add all services');
		this.togglePositionChange.emit(true);
		this.addedServices.push(...this.allServices);
		this.allServices = [];
	}

	private sortAllSelectedService(serviceData: DragDropServices[]) {
		return serviceData.sort((a, b) => a.name.localeCompare(b.name));
	}
}
